import React, { useState, useEffect } from 'react';
import { Grid2, Box, Button, Container, CircularProgress, InputLabel, MenuItem, FormControl, Select, FormHelperText } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const RentPreferencesForm = ({ apartmentId, token }) => {
  const { t } = useTranslation();
  const [rentPreferences, setRentPreferences] = useState({
    visa_status: false,
    student_status: false,
    animals: false,
    kids: false,
    couple: false,
    single: false,
  });
  const [isNewEntry, setIsNewEntry] = useState(true); // Assume new entry by default
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRentPreferences = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rentpreferences/${apartmentId}/`);
        setRentPreferences(response.data);
        setIsNewEntry(false);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // Not found means it's a new entry
          setIsNewEntry(true);
        } else {
          setError('Error fetching rent preferences details.');
          console.error('Error fetching rent preferences data:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    if (apartmentId) {
      fetchRentPreferences();
    }
  }, [apartmentId, token]);

  const handleChange = (field, value) => {
    setRentPreferences((prevPreferences) => ({
      ...prevPreferences,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/rentpreferences/${apartmentId}/`;
      const method = isNewEntry ? 'post' : 'put';
      const formData = new FormData();

      // Append form fields to formData
      for (const key in rentPreferences) {
        formData.append(key, rentPreferences[key] ? 'true' : 'false'); // Convert boolean to string
      }

      console.log('rent preferences formData', formData);

      await axios[method](url, formData, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Rent preferences saved successfully!');
    } catch (error) {
      console.error('Error saving rent preferences data:', error);
      alert('Failed to save rent preferences data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Grid2 container spacing={2}>
        {/* Form Fields Section */}
        <Grid2 container item xs={12} spacing={2}>
          {['visa_status', 'student_status', 'animals', 'kids', 'couple', 'single'].map((field) => (
            <Grid2 item xs={12} sm={6} md={4} key={field}>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t(`rentPreferences.${field}`)}</InputLabel>
                <Select
                  name={field}
                  value={rentPreferences[field] ? 'acceptable' : 'not acceptable'}
                  onChange={(e) => handleChange(field, e.target.value === 'acceptable')}
                  label={t(`rentPreferences.${field}`)}
                >
                  <MenuItem value="acceptable">{t('rentPreferences.acceptable')}</MenuItem>
                  <MenuItem value="not acceptable">{t('rentPreferences.notAcceptable')}</MenuItem>
                </Select>
                <FormHelperText>{t(`rentPreferences.helper.${field}`)}</FormHelperText>
              </FormControl>
            </Grid2>
          ))}
        </Grid2>

        {/* Error Message Section */}
        {error && (
          <Grid2 item xs={12}>
            <div style={{ color: 'red' }}>{error}</div>
          </Grid2>
        )}

        {/* Save Button Section */}
        <Grid2 item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {t('rentPreferences.save')}
          </Button>
        </Grid2>
      </Grid2>
    </Container>
  );
};

export default RentPreferencesForm;